import type { ComponentType } from "react";
import { Fragment } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CaretLeft,
  ChartBar,
  CurrencyGbp,
  Kanban,
  NotePencil,
  PushPin,
  Shield,
  SignOut,
  Target,
  User,
} from "@phosphor-icons/react";
import clsx from "clsx";

import { Avatar, AvatarFallback, Badge, Separator } from "@breakingthelines/design-system";

import { useModerationPendingCount } from "@/hooks/use-moderation-pending-count";
import { cookieLogout } from "@/lib/auth-api";
import { useAuthStore } from "@/stores/auth-store";

import logoMark from "@/assets/logo-mark.svg";

type NavItem = {
  label: string;
  to: string;
  icon: ComponentType<{ size?: number; weight?: "fill" | "regular" }>;
  match: (pathname: string) => boolean;
  /** Optional pending-count badge, e.g. the moderation queue. */
  badgeCount?: number;
};

type NavSection = {
  id: string;
  items: NavItem[];
};

/**
 * Thirteen flat rows collapsed into seven: Moderation, Discovery, Content and
 * Users & Access each used to list every child page as its own sidebar row.
 * Now each is a single row that opens its default child, and the section's
 * other pages are reached via the tab bar those pages share (see
 * section-tabs-layout.tsx) instead of via more sidebar rows. The three
 * standalone rows (Home, Campaigns, Analytics) are unchanged.
 *
 * Grouped into sections purely for the hairline dividers between them - every
 * section here happens to render as one row, but the grouping still reads as
 * "these belong together" the way Studio's sidebar sections do.
 */
function useNavSections(): NavSection[] {
  const moderationPendingCount = useModerationPendingCount();

  return [
    {
      id: "dashboard",
      items: [{ label: "Home", to: "/", icon: Kanban, match: (pathname) => pathname === "/" }],
    },
    {
      id: "moderation",
      items: [
        {
          label: "Moderation",
          to: "/moderation/queue",
          icon: Shield,
          match: (pathname) => pathname.startsWith("/moderation"),
          badgeCount: moderationPendingCount,
        },
      ],
    },
    {
      id: "discovery",
      items: [
        {
          label: "Discovery",
          to: "/search/editorial-overrides",
          icon: PushPin,
          match: (pathname) =>
            pathname.startsWith("/search/editorial-overrides") ||
            pathname.startsWith("/taxonomy") ||
            pathname.startsWith("/machine-tags") ||
            pathname.startsWith("/entities"),
        },
      ],
    },
    {
      id: "content",
      items: [
        {
          label: "Content",
          to: "/content",
          icon: NotePencil,
          match: (pathname) =>
            pathname === "/content" ||
            pathname.startsWith("/content/") ||
            pathname.startsWith("/pages"),
        },
      ],
    },
    {
      id: "users-access",
      items: [
        {
          label: "Users & Access",
          to: "/users",
          icon: User,
          match: (pathname) =>
            pathname.startsWith("/users") ||
            pathname.startsWith("/admins") ||
            pathname.startsWith("/advertisers") ||
            pathname.startsWith("/audit"),
        },
      ],
    },
    {
      id: "campaigns",
      items: [
        {
          label: "Campaigns",
          to: "/campaigns",
          icon: Target,
          match: (pathname) => pathname.startsWith("/campaigns"),
        },
      ],
    },
    {
      id: "revenue",
      items: [
        {
          label: "Revenue",
          to: "/revenue",
          icon: CurrencyGbp,
          match: (pathname) => pathname.startsWith("/revenue"),
        },
      ],
    },
    {
      id: "analytics",
      items: [
        {
          label: "Analytics",
          to: "/analytics/overview",
          icon: ChartBar,
          match: (pathname) => pathname.startsWith("/analytics"),
        },
      ],
    },
  ];
}

/**
 * One nav row, and the row the logout button copies. `min-h-11` is the 44px
 * touch floor - applied at every width rather than only below the breakpoint,
 * because the row is the same size in studio and a 44px target is not worse
 * with a mouse.
 */
const ROW_CLASS =
  "rounded-btl-sm relative isolate flex min-h-11 w-full items-center gap-2.5 px-3 text-sm font-semibold tracking-[-0.42px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500";

const ROW_RESTING_CLASS = "text-grey-500 hover:text-white";

type SidebarProps = {
  /**
   * Called when the user picks a destination. The shell uses it to dismiss the
   * mobile drawer; at desktop widths it is simply not supplied.
   */
  onNavigate?: () => void;
  /**
   * Supplied only at desktop widths, where the column can be collapsed. The
   * control renders in the logo row; when the column is collapsed the shell
   * renders its own copy on the rail that replaces it, since this one is
   * inside the part that just got clipped away.
   */
  onToggleCollapse?: () => void;
  /** Id of the element the collapse control expands and collapses. */
  collapseControls?: string;
};

export function Sidebar({ onNavigate, onToggleCollapse, collapseControls }: SidebarProps = {}) {
  const { user, session, clearSession } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const sections = useNavSections();

  const handleLogout = async () => {
    onNavigate?.();
    await cookieLogout(session?.token);
    clearSession();
    navigate("/login");
  };

  return (
    <aside data-slot="app-sidebar" className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between gap-2 px-5 pt-5 pb-4">
        <span className="flex items-center gap-2">
          <img src={logoMark} alt="" aria-hidden className="size-6 shrink-0" />
          <span className="text-[15px] font-semibold tracking-[-0.02em] text-white">Admin</span>
        </span>
        {onToggleCollapse ? (
          <SidebarCollapseToggle
            collapsed={false}
            onToggle={onToggleCollapse}
            controls={collapseControls}
          />
        ) : null}
      </div>

      <div className="px-4 pb-4">
        <div className="glass-card rounded-btl-sm flex items-center gap-3 px-3 py-2">
          <Avatar size="default">
            <AvatarFallback>{user?.name.slice(0, 2).toUpperCase() ?? "GF"}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="truncate text-sm font-semibold tracking-[-0.42px] text-white">
              {user?.name ?? "Gabriel Foligno"}
            </p>
            <p className="text-grey-500 truncate text-xs tracking-[-0.24px]">Admin</p>
          </div>
        </div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-4" aria-label="Main">
        {sections.map((section, index) => (
          <Fragment key={section.id}>
            {/* The design-system's Separator, which is where the `role="separator"`
                comes from - it is Base UI's, not hand-written here. One between
                each pair of groups, never after the last. */}
            {index > 0 ? (
              <div className="px-3 py-2">
                <Separator />
              </div>
            ) : null}
            <div data-slot="sidebar-group" className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = item.match(location.pathname);
                return (
                  // A plain Link, not NavLink: NavLink derives isActive (and
                  // therefore aria-current) from its own `to`/`end` matching
                  // against the location, which can't express "any /moderation/*
                  // route" - only an exact-or-descendant match on `to` itself.
                  // `active` here already accounts for that via each item's own
                  // `match`, so it drives className and aria-current directly.
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={onNavigate}
                    className={clsx(ROW_CLASS, active ? "text-white" : ROW_RESTING_CLASS)}
                    aria-current={active ? "page" : undefined}
                  >
                    {/* The active fill is a sibling layer rather than a background
                        on the row itself: `backdrop-filter` needs its own element
                        to blur what is behind it, and `-z-10` inside an `isolate`
                        row keeps it behind the label without escaping the row's
                        stacking context. */}
                    {active ? (
                      <span
                        aria-hidden
                        className="glass-card rounded-btl-sm absolute inset-0 -z-10"
                      />
                    ) : null}
                    <Icon size={16} weight={active ? "fill" : "regular"} />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {item.badgeCount !== undefined && item.badgeCount > 0 ? (
                      <Badge
                        variant="tintedBrand"
                        className="shrink-0 rounded-full"
                        aria-label={`${item.badgeCount} pending`}
                      >
                        {item.badgeCount > 99 ? "99+" : item.badgeCount}
                      </Badge>
                    ) : null}
                  </Link>
                );
              })}
            </div>
          </Fragment>
        ))}
      </nav>

      <div className="border-border mt-2 border-t px-4 py-4">
        <button
          type="button"
          className={clsx(ROW_CLASS, ROW_RESTING_CLASS, "cursor-pointer")}
          onClick={handleLogout}
        >
          <SignOut size={16} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}

type SidebarCollapseToggleProps = {
  collapsed: boolean;
  onToggle: () => void;
  controls?: string;
};

/**
 * The collapse control, rendered twice by design: once in the sidebar's own
 * logo row while the column is open, and once on the narrow rail the shell puts
 * in the column's place once it is collapsed. Only ever one of the two is in
 * the document.
 *
 * Studio's equivalent carries an `aria-label` and nothing else. This one also
 * reports its state, so a screen reader user can tell whether the nav is there
 * before going looking for it.
 */
export function SidebarCollapseToggle({
  collapsed,
  onToggle,
  controls,
}: SidebarCollapseToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
      aria-expanded={!collapsed}
      aria-controls={controls}
      className="glass-card rounded-btl-sm text-grey-500 flex size-7 shrink-0 cursor-pointer items-center justify-center transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-500"
    >
      <CaretLeft size={14} weight="bold" className={collapsed ? "rotate-180" : undefined} />
    </button>
  );
}
